import axios from "axios";
import { useMutation, useQuery } from "react-query";

import { nuclearn_api_url } from "src/App";
import type { Part } from "src/views/products/parts-ai/services/PartService";

/**
 * Catalog values frozen onto the record at kickoff. `manufacturer_id`/`plant_id`
 * are the persisted snapshot's wire names for the catalog's `*_fk` keys, so
 * they stay even though the rest of the shape follows `Part`.
 */
export type PartSnapshot = Pick<
  Part,
  | "id"
  | "name"
  | "material_number"
  | "part_type"
  | "manufacturer"
  | "description"
  | "features"
  | "feature_order"
> & {
  manufacturer_id?: Part["manufacturer_fk"];
  plant_id?: Part["plant_fk"];
  /** Raw values only for the feature keys that unit conversion rewrote. */
  features_original?: Record<string, string> | null;
};

/** Record lifecycle. `package` is the last stage for every conclusion, so
 * `complete` is the only terminal state — polling keys off that. */
export type ItemEquivalencyRecordStatus = "draft" | "in_progress" | "complete";

export interface ItemEquivalencyStage {
  key: string;
  label: string;
  status: "pending" | "active" | "complete";
}

export interface OpenEvidenceItem {
  characteristic: string;
  rating: string;
  needed_evidence: string[];
}

export interface ItemEquivalencyRecord {
  id: number;
  title: string;
  status: ItemEquivalencyRecordStatus;
  plant_id?: number | null;
  suggested_plant_id?: number | null;
  original_part_id: number;
  replacement_part_id: number;
  // Nullable: evaluations started before catalog parts existed (or outside
  // the picker flow) have no snapshots — ItemEquivalencyPartsTab handles both
  // being absent.
  original_part_snapshot: PartSnapshot | null;
  replacement_part_snapshot: PartSnapshot | null;
  dialogue_id: number;
  create_user_id: number;
  create_date?: string | null;
  stages: ItemEquivalencyStage[];
  open_items: OpenEvidenceItem[];
  comparison_row_count: number;
  /** Rows recorded as dedication controls: documented in the package, never
   * compared, and never open items. */
  documented_row_count?: number;
  computed_verdict: string;
}

/**
 * The document-scope keys this product persists into `dialogues.config`.
 * The dialogue service is untyped JS, so readers cast through this type
 * instead of an inline shape a rename would silently miss.
 */
export interface PersistedDialogueConfig {
  source_data_record_ids?: number[];
  dataset_ids?: number[];
}

export interface StartItemEquivalencyPayload {
  originalPartId: number;
  replacementPartId: number;
  chatConfigId?: number | null;
  /** Pre-created dialogue (from staged document uploads) to attach the record to. */
  dialogueId?: number | null;
  /** The documents the engineer picked; stored with the evaluation so a page reload cannot lose them. */
  sourceDataRecordIds?: number[];
  /** The datasets to search for reference material; stored with the evaluation for the same reason. */
  datasetIds?: number[];
}

const startItemEquivalency = async (
  payload: StartItemEquivalencyPayload,
): Promise<ItemEquivalencyRecord> => {
  const response = await axios.post<ItemEquivalencyRecord>(
    `${nuclearn_api_url}/products/item-equivalency/records/from-parts`,
    {
      original_part_id: payload.originalPartId,
      replacement_part_id: payload.replacementPartId,
      chat_config_id: payload.chatConfigId ?? null,
      dialogue_id: payload.dialogueId ?? null,
      source_data_record_ids: payload.sourceDataRecordIds ?? [],
      dataset_ids: payload.datasetIds ?? [],
    },
  );
  return response.data;
};

export const useStartItemEquivalency = () =>
  useMutation((payload: StartItemEquivalencyPayload) =>
    startItemEquivalency(payload),
  );

const getItemEquivalencyRecord = async (
  dialogueId: number,
): Promise<ItemEquivalencyRecord> => {
  const response = await axios.get<ItemEquivalencyRecord>(
    `${nuclearn_api_url}/products/item-equivalency/records/by-dialogue/${dialogueId}`,
  );
  return response.data;
};

export const useItemEquivalencyRecord = (dialogueId?: number) =>
  useQuery(
    ["itemEquivalencyRecord", dialogueId],
    () => getItemEquivalencyRecord(dialogueId!),
    {
      enabled: Boolean(dialogueId),
      retry: false,
      refetchInterval: (data) =>
        data && data.status !== "complete" ? 3000 : false,
      refetchIntervalInBackground: false,
    },
  );

export interface ItemEquivalencyRunListItem {
  record_id: number;
  dialogue_id: number | null;
  title: string;
  status: ItemEquivalencyRecordStatus;
  conclusion: string | null;
  open_item_count: number;
  report_artifact_fk: number | null;
  create_date: string | null;
  updated_date: string | null;
}

const getItemEquivalencyRuns = async (): Promise<
  ItemEquivalencyRunListItem[]
> => {
  const response = await axios.get<{ runs: ItemEquivalencyRunListItem[] }>(
    `${nuclearn_api_url}/products/item-equivalency/records`,
  );
  return response.data.runs;
};

export const useItemEquivalencyRuns = () =>
  useQuery(["itemEquivalencyRuns"], getItemEquivalencyRuns, {
    retry: false,
  });

/** Fetch the recorded §3.5.4 package markdown for preview. */
export const getPackageMarkdown = async (
  artifactId: number,
): Promise<string> => {
  const response = await axios.get(
    `${nuclearn_api_url}/artifacts/${artifactId}/download`,
    { responseType: "text", transformResponse: [(data) => data] },
  );
  return String(response.data);
};
